import { createTheme, darken, lighten, type Direction, type Theme } from '@mui/material/styles';
import { arEG } from '@mui/material/locale';
import { arSD as gridArabic } from '@mui/x-data-grid/locales';

/**
 * 'Warm Sand' — the visual direction chosen for Diwan.
 *
 * Cream surfaces, a terracotta accent close to the restaurant brand, rounded
 * pill buttons and generous nav rows. Manrope carries Latin text and Cairo
 * carries Arabic; both are self-hosted (see main.tsx) so the PWA renders the
 * same offline. Arabic mirrors the entire layout, so direction is a theme
 * concern rather than a per-component override.
 *
 * Every value here is from DESIGN-SPEC.md §1; the HR and Finance apps share
 * the list so the two render identically for the same tenant.
 */

export const SAND = {
  accent: '#c2410c',
  accentLight: '#ea580c',
  accentDark: '#9a3412',
  accentTint: '#fbe3d5',
  canvas: '#faf6f0',
  paper: '#fffdfa',
  sidebar: '#f4ede3',
  head: '#f7f1e8',
  ink: '#292524',
  muted: '#78716c',
  faint: '#a8a29e',
  line: '#eadfd2',
  lineSoft: '#f1eae0',
  lineCard: '#efe6da',
  /** Input and pill borders. */
  field: '#e0d5c7',
  /** Body text of nav items and outlined buttons. */
  inkSoft: '#44403c',
  /** Hover of a nav row and a clickable table row. */
  hover: '#ece3d6',
  rowHover: '#fbf7f1',
  success: '#3f6212',
  warning: '#a16207',
  error: '#b91c1c',
  info: '#0e7490',
  infoDark: '#155e75',
  /** Chip and callout tints. */
  okTint: '#e4efd6',
  warnTint: '#fdf1d2',
  errTint: '#fde2e2',
  infoTint: '#d9eef3',
  neutralTint: '#f0e9df',
  neutralText: '#57534e',
  /** Callout borders: a shade darker than their tint. */
  okLine: '#c9dcb0',
  warnLine: '#f1d9a0',
  errLine: '#f3b4b4',
  infoLine: '#bfe0e8',
  /** The sidebar's app tag. */
  tagBg: '#e7ddd0',
  tagText: '#57534e',
} as const;

export const CARD_SHADOW = '0 1px 3px rgba(87,60,30,.08)';

// The accent's tint rides along in the palette, so a component can paint a
// tinted background from the theme instead of recomputing it from `main`.
declare module '@mui/material/styles' {
  interface PaletteColor {
    tint?: string;
  }
  interface SimplePaletteColorOptions {
    tint?: string;
  }
}

/**
 * A palette derived from one colour.
 *
 * The tenant's brand replaces the accent everywhere the accent appears —
 * buttons, links, the selected nav row, focus rings — and its light and dark
 * variants are computed rather than asked for, because a customer supplies one
 * colour, not three. An unparseable value falls back to the product's own.
 */
export function accentFrom(color?: string | null): {
  main: string;
  light: string;
  dark: string;
  tint: string;
} {
  if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) {
    return { main: SAND.accent, light: SAND.accentLight, dark: SAND.accentDark, tint: SAND.accentTint };
  }
  return {
    main: color,
    light: lighten(color, 0.18),
    dark: darken(color, 0.22),
    tint: lighten(color, 0.82),
  };
}

export function buildTheme(direction: Direction, brandColor?: string | null): Theme {
  const accent = accentFrom(brandColor);
  // Component-internal strings (pagination, sort menus, column menus) come from
  // the MUI locale packs; everything the app itself renders goes through t().
  const gridExtras = {
    components: {
      MuiDataGrid: {
        defaultProps: {
          localeText: { columnMenuAriaLabel: (column: string) => `قائمة عمود ${column}` },
        },
      },
    },
  };
  const locales = direction === 'rtl' ? [arEG, gridArabic, gridExtras] : [];
  /** The focus ring every field and pill shares: the accent, tinted. */
  const focusRing = `0 0 0 2px ${accent.tint}`;
  return createTheme(
    {
      direction,
      palette: {
        mode: 'light',
        primary: {
          main: accent.main,
          light: accent.light,
          dark: accent.dark,
          tint: accent.tint,
          contrastText: '#ffffff',
        },
        secondary: { main: SAND.muted },
        success: { main: SAND.success, tint: SAND.okTint },
        warning: { main: SAND.warning, tint: SAND.warnTint },
        error: { main: SAND.error, tint: SAND.errTint },
        info: { main: SAND.info, dark: SAND.infoDark, tint: SAND.infoTint },
        background: { default: SAND.canvas, paper: SAND.paper },
        text: { primary: SAND.ink, secondary: SAND.muted, disabled: SAND.faint },
        divider: SAND.line,
      },
      typography: {
        fontFamily:
          direction === 'rtl'
            ? '"Cairo Variable","Manrope Variable",system-ui,sans-serif'
            : '"Manrope Variable","Cairo Variable",system-ui,sans-serif',
        fontSize: 14,
        // h4 is the page title, h6 the card title; the rest step between them.
        h4: { fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25 },
        h5: { fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 },
        h6: { fontSize: 16, fontWeight: 700, lineHeight: 1.4 },
        subtitle1: { fontWeight: 700 },
        subtitle2: { fontWeight: 700 },
        body1: { fontSize: 14, lineHeight: 1.45 },
        body2: { fontSize: 13, lineHeight: 1.45 },
        caption: { fontSize: 12, lineHeight: 1.4 },
        overline: { fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none', lineHeight: 1.4 },
        button: { textTransform: 'none', fontWeight: 700 },
      },
      shape: { borderRadius: 14 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: { backgroundColor: SAND.canvas, color: SAND.ink },
            // DataGrid is themed through global CSS: it is not in MUI core's
            // component map, and the X augmentation would only add types.
            '.MuiDataGrid-root': {
              '--DataGrid-containerBackground': SAND.head,
              '--DataGrid-rowBorderColor': SAND.lineSoft,
              '--DataGrid-overlayHeight': '240px',
              backgroundColor: SAND.paper,
              borderColor: SAND.line,
              borderRadius: 14,
              fontSize: 14,
            },
            '.MuiDataGrid-columnHeaders': {
              borderBottom: `1px solid ${SAND.line}`,
            },
            '.MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
              paddingInline: 12,
            },
            '.MuiDataGrid-columnHeaderTitle': {
              color: SAND.muted,
              fontWeight: 700,
              fontSize: 13,
            },
            '.MuiDataGrid-cell': {
              borderBottom: `1px solid ${SAND.lineSoft}`,
            },
            '.MuiDataGrid-cell--textRight, .MuiDataGrid-columnHeader--alignRight': {
              fontVariantNumeric: 'tabular-nums',
            },
            '.MuiDataGrid-row:hover': {
              backgroundColor: `${SAND.rowHover} !important`,
            },
            '.MuiDataGrid-row.Mui-selected': {
              backgroundColor: `${accent.tint} !important`,
            },
            '.MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${SAND.line}`,
              backgroundColor: SAND.head,
            },
            '.MuiDataGrid-toolbarContainer': {
              padding: '8px 12px',
              borderBottom: `1px solid ${SAND.line}`,
            },
            // The tree view (departments) reads as the prototype's indented rows.
            '.MuiTreeItem-content': {
              borderRadius: 10,
              paddingBlock: 6,
            },
            '.MuiTreeItem-content.Mui-selected, .MuiTreeItem-content.Mui-selected.Mui-focused': {
              backgroundColor: `${accent.tint} !important`,
              color: accent.dark,
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 14,
              lineHeight: 1.45,
              paddingBlock: 7,
              paddingInline: 18,
              whiteSpace: 'nowrap',
              '&.Mui-disabled': { opacity: 0.45 },
              '&.Mui-focusVisible': { boxShadow: focusRing },
            },
            sizeSmall: { paddingBlock: 4, paddingInline: 12, fontSize: 12 },
            sizeLarge: { paddingBlock: 9, paddingInline: 22, fontSize: 15 },
            // Outlined is the prototype's plain white pill; the accent only
            // fills the primary action of a toolbar.
            outlined: {
              backgroundColor: '#ffffff',
              borderColor: SAND.field,
              color: SAND.inkSoft,
              '&:hover': { backgroundColor: SAND.rowHover, borderColor: SAND.field },
              '&.Mui-disabled': { borderColor: SAND.field, color: SAND.inkSoft },
            },
            outlinedPrimary: {
              color: accent.main,
              borderColor: SAND.field,
              '&:hover': { borderColor: accent.main, backgroundColor: accent.tint },
            },
            outlinedError: {
              color: SAND.error,
              borderColor: SAND.errLine,
              '&:hover': { backgroundColor: SAND.errTint, borderColor: SAND.errLine },
            },
            containedPrimary: {
              '&.Mui-disabled': { backgroundColor: accent.main, color: '#ffffff' },
            },
            text: { paddingInline: 12 },
            textPrimary: { color: accent.dark },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              '&.Mui-focusVisible': { boxShadow: focusRing },
            },
          },
        },
        MuiButtonGroup: {
          defaultProps: { disableElevation: true },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
              borderColor: SAND.field,
              '&.Mui-selected': { backgroundColor: accent.tint, color: accent.dark },
              '&.Mui-selected:hover': { backgroundColor: accent.tint },
            },
          },
        },
        MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
        MuiSelect: { defaultProps: { size: 'small' } },
        MuiFormControl: { defaultProps: { size: 'small' } },
        MuiAppBar: {
          styleOverrides: {
            root: { backgroundColor: SAND.canvas, color: SAND.ink },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            // The header is 60px at every width; the default grows on desktop.
            root: { minHeight: 60, '@media (min-width:600px)': { minHeight: 60 } },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: SAND.sidebar,
              borderRight: 'none',
              borderLeft: 'none',
              '& .MuiDivider-root': { borderColor: '#e6dccd' },
              '& .MuiListSubheader-root': {
                fontSize: 12,
                fontWeight: 700,
                color: SAND.faint,
                lineHeight: '34px',
                paddingInline: 18,
                backgroundColor: 'transparent',
              },
              '& .MuiListItemButton-root': {
                borderRadius: 10,
                margin: '2px 10px',
                paddingBlock: 8,
                paddingInline: 12,
                color: SAND.inkSoft,
                '& .MuiListItemIcon-root': { color: SAND.muted, minWidth: 32 },
                '& .MuiSvgIcon-root': { fontSize: 20 },
                '&:hover': { backgroundColor: SAND.hover },
              },
              '& .MuiListItemButton-root.Mui-selected': {
                backgroundColor: accent.tint,
                color: accent.dark,
                '&:hover': { backgroundColor: accent.tint },
                '& .MuiListItemIcon-root': { color: accent.main },
                '& .MuiListItemText-primary': { fontWeight: 700 },
              },
            },
          },
        },
        MuiListSubheader: {
          styleOverrides: {
            root: { fontSize: 12, fontWeight: 700, color: SAND.faint, lineHeight: '34px' },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none' },
            outlined: { borderColor: SAND.lineCard },
            elevation1: {
              boxShadow: CARD_SHADOW,
              border: `1px solid ${SAND.lineCard}`,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: CARD_SHADOW,
              border: `1px solid ${SAND.lineCard}`,
              borderRadius: 14,
              backgroundColor: SAND.paper,
            },
          },
        },
        MuiCardContent: {
          styleOverrides: {
            root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
          },
        },
        MuiCardHeader: {
          styleOverrides: {
            root: { padding: '20px 20px 0' },
            title: { fontSize: 16, fontWeight: 700 },
          },
        },
        MuiCardActions: {
          styleOverrides: { root: { padding: '0 20px 20px' } },
        },
        MuiTableContainer: {
          styleOverrides: {
            root: {
              backgroundColor: SAND.paper,
              border: `1px solid ${SAND.line}`,
              borderRadius: 14,
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              padding: '8px 12px',
              borderBottom: `1px solid ${SAND.lineSoft}`,
              fontSize: 14,
              lineHeight: 1.45,
              '&.MuiTableCell-alignRight': { fontVariantNumeric: 'tabular-nums' },
            },
            head: {
              backgroundColor: SAND.head,
              color: SAND.muted,
              fontWeight: 700,
              fontSize: 13,
              padding: '9px 12px',
              borderBottom: `1px solid ${SAND.line}`,
            },
            sizeSmall: { padding: '6px 10px' },
            footer: { fontWeight: 800, backgroundColor: SAND.head, color: SAND.ink, borderBottom: 'none' },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-hover:hover': { backgroundColor: SAND.rowHover },
              // A card's table ends with the card, not with a stray rule.
              '&:last-child .MuiTableCell-body': { borderBottom: 'none' },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 12,
              height: 'auto',
              minHeight: 22,
              // A DataGrid cell sets line-height to the row height; an auto-height
              // chip would inherit it and grow to 52px. Pin the body line height.
              lineHeight: 1.45,
              alignSelf: 'center',
              backgroundColor: SAND.neutralTint,
              color: SAND.neutralText,
            },
            label: { paddingInline: 10, paddingBlock: 2 },
            sizeSmall: { minHeight: 20 },
            labelSmall: { paddingInline: 10, paddingBlock: 1 },
            icon: { marginInlineStart: 6, marginInlineEnd: -4, fontSize: 16 },
            deleteIcon: { fontSize: 16, marginInlineEnd: 6 },
            // MUI's colour names map onto the spec's tints rather than solid fills.
            colorPrimary: { backgroundColor: accent.tint, color: accent.dark },
            colorSecondary: { backgroundColor: SAND.neutralTint, color: SAND.neutralText },
            colorSuccess: { backgroundColor: SAND.okTint, color: SAND.success },
            colorWarning: { backgroundColor: SAND.warnTint, color: SAND.warning },
            colorError: { backgroundColor: SAND.errTint, color: SAND.error },
            colorInfo: { backgroundColor: SAND.infoTint, color: SAND.infoDark },
            outlined: { backgroundColor: '#ffffff', borderColor: SAND.line },
            outlinedPrimary: { color: accent.dark, borderColor: accent.light },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: '#ffffff',
              borderRadius: 10,
              '&.Mui-focused': { boxShadow: focusRing },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accent.main, borderWidth: 1 },
              '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                borderColor: '#cdbfae',
              },
              // Labels sit above the field (see MuiInputLabel), so the outline
              // has no notch to cut.
              '& .MuiOutlinedInput-notchedOutline': { top: 0 },
              '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            },
            notchedOutline: { borderColor: SAND.field },
            input: { paddingBlock: 8.5, paddingInline: 12 },
            inputSizeSmall: { paddingBlock: 7, paddingInline: 11 },
            inputMultiline: { paddingBlock: 0 },
          },
        },
        MuiInputLabel: {
          defaultProps: { shrink: true },
          styleOverrides: {
            // The prototype's "label above the input" form: static, 12/700 muted.
            root: {
              position: 'relative',
              transform: 'none',
              fontSize: 12,
              fontWeight: 700,
              color: SAND.muted,
              lineHeight: 1.4,
              marginBottom: 4,
              maxWidth: '100%',
              '&.Mui-focused': { color: accent.dark },
              '&.Mui-error': { color: SAND.error },
              '&.Mui-disabled': { color: SAND.faint },
            },
            asterisk: { color: SAND.error },
          },
        },
        MuiFormHelperText: {
          styleOverrides: { root: { marginInline: 2, fontSize: 12 } },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: { fontSize: 12, fontWeight: 700, color: SAND.muted, '&.Mui-focused': { color: accent.dark } },
          },
        },
        MuiInputAdornment: {
          styleOverrides: { root: { color: SAND.muted } },
        },
        MuiAutocomplete: {
          styleOverrides: {
            inputRoot: { paddingBlock: 2 },
            paper: { borderRadius: 12, border: `1px solid ${SAND.lineCard}`, boxShadow: '0 8px 24px rgba(41,37,36,.12)' },
            option: { '&[aria-selected="true"]': { backgroundColor: `${accent.tint} !important` } },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: { borderRadius: 12, border: `1px solid ${SAND.lineCard}`, boxShadow: '0 8px 24px rgba(41,37,36,.12)' },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              marginInline: 6,
              paddingBlock: 7,
              '&.Mui-selected': { backgroundColor: accent.tint, color: accent.dark },
              '&.Mui-selected:hover': { backgroundColor: accent.tint },
            },
          },
        },
        MuiTabs: {
          // A row of tabs scrolls rather than clips on a phone; the arrows show
          // there too, since a hidden tab is one nobody opens.
          defaultProps: { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true },
          styleOverrides: {
            root: { minHeight: 0, borderBottom: `1px solid ${SAND.line}` },
            indicator: { height: 3, borderRadius: 3 },
            flexContainer: { gap: 4 },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              minHeight: 0,
              padding: '9px 14px',
              fontWeight: 600,
              fontSize: 14,
              color: SAND.muted,
              '&.Mui-selected': { color: accent.dark },
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: { borderRadius: 18, boxShadow: '0 20px 60px rgba(0,0,0,.25)', backgroundColor: SAND.paper },
          },
        },
        MuiDialogTitle: {
          styleOverrides: { root: { fontSize: 18, fontWeight: 700, padding: '22px 22px 14px' } },
        },
        MuiDialogContent: {
          styleOverrides: { root: { padding: '0 22px 8px' } },
        },
        MuiDialogActions: {
          styleOverrides: { root: { padding: '16px 22px 22px', gap: 8, '& > :not(:first-of-type)': { marginInlineStart: 0 } } },
        },
        MuiAlert: {
          styleOverrides: {
            root: { borderRadius: 12, padding: '6px 14px', fontSize: 14, alignItems: 'center' },
            message: { padding: '4px 0' },
            icon: { padding: '4px 0', marginInlineEnd: 10, marginRight: 0 },
            action: { paddingTop: 0, marginRight: 0, marginInlineEnd: -6, marginInlineStart: 'auto' },
            standardInfo: { backgroundColor: SAND.infoTint, color: SAND.infoDark, border: `1px solid ${SAND.infoLine}` },
            standardSuccess: { backgroundColor: SAND.okTint, color: SAND.success, border: `1px solid ${SAND.okLine}` },
            standardWarning: { backgroundColor: SAND.warnTint, color: SAND.warning, border: `1px solid ${SAND.warnLine}` },
            standardError: { backgroundColor: SAND.errTint, color: SAND.error, border: `1px solid ${SAND.errLine}` },
          },
        },
        MuiAlertTitle: {
          styleOverrides: { root: { fontWeight: 700, marginBottom: 2 } },
        },
        MuiBadge: {
          styleOverrides: {
            badge: { fontWeight: 700, fontSize: 11, minWidth: 18, height: 18, borderRadius: 999 },
          },
        },
        MuiAvatar: {
          styleOverrides: {
            root: { fontWeight: 700, fontSize: 13, backgroundColor: accent.tint, color: accent.dark },
          },
        },
        MuiLinearProgress: {
          styleOverrides: { root: { borderRadius: 999, backgroundColor: SAND.lineSoft } },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
          },
        },
        MuiCheckbox: {
          styleOverrides: { root: { color: SAND.faint } },
        },
        MuiRadio: {
          styleOverrides: { root: { color: SAND.faint } },
        },
        MuiLink: {
          defaultProps: { underline: 'hover' },
          styleOverrides: { root: { fontWeight: 600 } },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: SAND.ink,
              borderRadius: 8,
              fontSize: 12.5,
            },
            arrow: { color: SAND.ink },
          },
        },
        MuiSkeleton: {
          styleOverrides: { root: { backgroundColor: SAND.lineSoft } },
        },
        MuiDivider: {
          styleOverrides: { root: { borderColor: SAND.line } },
        },
        MuiAccordion: {
          defaultProps: { disableGutters: true, elevation: 0 },
          styleOverrides: {
            root: {
              border: `1px solid ${SAND.lineCard}`,
              borderRadius: 14,
              '&::before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
            },
          },
        },
        MuiStepIcon: {
          styleOverrides: { root: { color: SAND.line, '&.Mui-active': { color: accent.main }, '&.Mui-completed': { color: SAND.success } } },
        },
      },
    },
    ...locales,
  );
}
